import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ProfitByPeriodApiResponse,
  ProfitCalculationProfitData,
} from '../../../Detail/Charts/types';
import useRequest from '../../../../hooks/useRequest';
import ServicePaths from '../../../../bridge/utils/ServicePaths';
import SpcResultView from '../../../Subscription/Content/Calculations/SpcResultView';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { colors } from '../../../../colors';
import DropdownComponent from '../../../../components/Form/Dropdown';
import moment from 'moment';
import { ResultOverviewFile } from 'grts-utils/types/ProfitCalcs';
import { SingleProfitCalculationApiResponse } from '../../types';
import LoadingIcons from 'react-loading-icons';
import ProfitDistributionChart from './ProfitDistributionChart';
import { ComponentDimensions } from '../../../../types/Layout';

interface Props {
  setProfitByPeriodData: React.Dispatch<
    React.SetStateAction<ProfitByPeriodApiResponse | undefined>
  >;
  setProfitData: React.Dispatch<
    React.SetStateAction<ProfitCalculationProfitData | undefined>
  >;
}

const CalculationResultHistory = (props: Props) => {
  const { setProfitByPeriodData, setProfitData } = props;

  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(undefined);
  const rootRef = useRef<any>(null);

  useLayoutEffect(() => {
    if (!rootRef.current) {
      return;
    }

    if (!!rootDimensions) {
      return;
    }

    setRootDimensions({
      height: rootRef.current.clientHeight,
      width: rootRef.current.clientWidth,
    });
  }, [rootDimensions]);

  const [shouldFetch, setShouldFetch] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isCollapsable, setIsCollapsable] = useState(false);

  const [baseAsset, setBaseAsset] = useState('');
  const [fileName, setFileName] = useState('');
  const [timeFrame, setTimeFrame] = useState('');
  const [timeFrameCombination, setTimeFrameCombination] = useState('');

  const toggleCollapse = useCallback(() => {
    if (!isCollapsable) {
      return;
    }

    setIsCollapsed(!isCollapsed);
  }, [isCollapsable, isCollapsed]);

  const params = useMemo(() => ({}), []);

  const options = useMemo<{ method: 'POST' }>(
    () => ({
      method: 'POST',
      shouldFetch: true,
    }),
    [],
  );

  const {
    data: fileNames,
    isValidating,
  }: {
    data: Record<string, string[]> | undefined;
    isValidating: boolean;
  } = useRequest(
    ServicePaths.PROFIT_CALCULATIONS.GetCalculationResultHistoryFiles,
    params,
    options,
  );

  const baseAssetsOptions = fileNames ? Object.keys(fileNames) : [];
  const fileNameOptions = fileNames && baseAsset ? fileNames[baseAsset] : [];

  const setFirstFileName = useCallback(
    (baseAsset: string) => {
      const files = fileNames?.[baseAsset] ?? [];
      files.sort((a, b) => {
        const timestampA = Number(a.split('_')[0]);
        const timestampB = Number(b.split('_')[0]);
        return timestampB - timestampA;
      });
      setFileName(files[0]);
    },
    [fileNames],
  );

  useEffect(() => {
    if (!baseAsset) {
      return;
    }

    setFirstFileName(baseAsset);
  }, [baseAsset, setFirstFileName]);

  useEffect(() => {
    if (fileName && !isCollapsable) {
      setIsCollapsable(true);
      setIsCollapsed(true);
    }
  }, [fileName, isCollapsable]);

  const fileResultParams = useMemo(
    () => ({ baseAsset, fileName }),
    [baseAsset, fileName],
  );

  const fileResultOptions = useMemo<{ method: 'POST' }>(
    () => ({
      method: 'POST',
      shouldFetch: !!fileName && fileNames?.[baseAsset].includes(fileName),
    }),
    [baseAsset, fileName, fileNames],
  );

  const {
    data: fileResult,
    isValidating: isValidatingFileResult,
  }: {
    data: ResultOverviewFile | undefined;
    isValidating: boolean;
  } = useRequest(
    ServicePaths.PROFIT_CALCULATIONS.GetCalculationResult,
    fileResultParams,
    fileResultOptions,
  );

  const timeFrameOptions = useMemo(
    () => (!!fileResult?.timeFrames ? Object.keys(fileResult?.timeFrames) : []),
    [fileResult?.timeFrames],
  );
  const timeFrameCombinationOptions = useMemo(
    () =>
      !!fileResult?.timeFrames?.[timeFrame]
        ? Object.keys(fileResult?.timeFrames[timeFrame])
        : [],
    [fileResult?.timeFrames, timeFrame],
  );

  useEffect(() => {
    if (!fileResult) {
      return;
    }

    if (fileResult.bestResult) {
      setTimeFrame(fileResult.bestResult.params.higherTimeFrame);
      setTimeFrameCombination(
        `${fileResult.bestResult.params.higherTimeFrame}_${fileResult.bestResult.params.shorterTimeFrame}_${fileResult.bestResult.params.multiOrderTimeFrame}`,
      );
      setShouldFetch(true);
      return;
    }

    // Take timeframe out of timeframes key
    const timeFrames = fileResult.timeFrames
      ? Object.keys(fileResult.timeFrames)
      : [];
    setTimeFrame(timeFrames[0]);
  }, [fileResult]);

  useEffect(() => {
    if (!timeFrame || !fileResult || !!timeFrameCombination) {
      return;
    }

    const timeFrameCombinations = fileResult.timeFrames?.[timeFrame]
      ? Object.keys(fileResult.timeFrames?.[timeFrame])
      : [];
    setTimeFrameCombination(timeFrameCombinations[0]);
    setShouldFetch(true);
  }, [timeFrame, setFirstFileName, fileResult, timeFrameCombination]);

  const spcParams = useMemo(() => {
    if (!fileResult || !timeFrame || !timeFrameCombination) {
      return undefined;
    }

    const result =
      fileResult.timeFrames?.[timeFrame]?.[timeFrameCombination]?.bestResult;

    if (!result) {
      return undefined;
    }

    return {
      baseAsset: result.params.baseAsset,
      quoteAsset: result.params.quoteAsset,
      exchange: result.params.exchange,
      startTimestamp: new Date(result.params.startTimestamp).valueOf(),
      endTimestamp: Date.now(),
      higherTimeFrame: result.params.higherTimeFrame,
      shorterTimeFrame: result.params.shorterTimeFrame,
      multiOrderTimeFrame: result.params.multiOrderTimeFrame,
      cbrt: result.params.cbrt,
      shorterTimeFrameInterval: result.params.shorterTimeFrameInterval,
      supportCbrtRange: result.params.supportCbrtRange,
      supportMrcState: result.params.supportMrcState,
      useTakeProfitTransactions: !!result.indicatorConfig,
      indicatorConfig: result.indicatorConfig,
      mrcRsiConfig: result.mrcRsiConfig,
    };
  }, [fileResult, timeFrame, timeFrameCombination]);

  const spcOptions = useMemo<{ method: 'POST' }>(
    () => ({
      method: 'POST',
      shouldFetch: !!spcParams && shouldFetch,
    }),
    [spcParams, shouldFetch],
  );

  const {
    data: spcResult,
    isValidating: isValidatingSpcResult,
  }: {
    data: SingleProfitCalculationApiResponse | undefined;
    isValidating: boolean;
  } = useRequest(
    ServicePaths.PROFIT_CALCULATIONS.GetSingleProfitCalculation,
    spcParams,
    spcOptions,
  );

  useEffect(() => {
    if (!spcResult) {
      return;
    }

    setProfitData({
      stable: spcResult.totalTradingStableProfit,
      asset: spcResult.totalTradingAssetProfit,
      price: spcResult.totalWithoutTradingStableProfit,
      priceSinceFirstTransaction:
        spcResult.totalWithoutTradingStableProfitSinceFirstTransaction,
    });

    setProfitByPeriodData({
      stable: Object.entries(spcResult.endBalanceInUsdtHistory).map(
        ([timestamp, value]) => ({ timestamp: parseInt(timestamp, 10), value }),
      ),
      asset: Object.entries(spcResult.endBalanceInAssetHistory).map(
        ([timestamp, value]) => ({ timestamp: parseInt(timestamp, 10), value }),
      ),
    });
  }, [spcResult, setProfitByPeriodData, setProfitData]);

  const handleGridItemClick = useCallback((timeFrameCombination: string) => {
    setShouldFetch(false);
    setTimeFrame(timeFrameCombination.split('_')[0]);
    setTimeFrameCombination(timeFrameCombination);
    setShouldFetch(true);
  }, []);

  return (
    <>
      <div
        className="single-profit-calculation-header"
        onClick={toggleCollapse}
      >
        <h3
          className="single-profit-calculation-label"
          style={{ marginLeft: 24 }}
        >
          Form:
        </h3>
        {isCollapsable &&
          (isCollapsed ? (
            <FaChevronDown
              color={colors.white}
              size={24}
              className={'chevron chevron-down'}
            />
          ) : (
            <FaChevronUp
              color={colors.white}
              size={24}
              className={'chevron chevron-down'}
            />
          ))}
      </div>

      {!isCollapsed && (
        <div
          style={{
            marginLeft: 24,
            marginBottom: 24,
            display: 'flex',
            columnGap: 16,
          }}
        >
          <DropdownComponent
            config={{
              label: 'Asset',
              value: baseAsset,
              handleValueChange: (baseAsset: string) => {
                setShouldFetch(false);
                setBaseAsset(baseAsset);
              },
              options: baseAssetsOptions,
              shouldFit: true,
            }}
          />
          <DropdownComponent
            config={{
              label: 'File',
              value: fileName,
              handleValueChange: (fileName: string) => {
                setShouldFetch(false);
                setFileName(fileName);
                setTimeFrame('');
                setTimeFrameCombination('');
              },
              options: fileNameOptions,
              shouldFit: true,
              labelFormatter: (option: string) => {
                if (!option?.length) {
                  return option;
                }

                const timestamp = Number(option.split('_')[0]);
                const date = new Date(timestamp);
                return `${moment(date).format('DD MMM YYYY | HH:mm')} `;
              },
            }}
          />

          <DropdownComponent
            config={{
              label: 'TimeFrame',
              value: timeFrame,
              handleValueChange: (timeFrame: string) => {
                setTimeFrameCombination('');
                setTimeFrame(timeFrame);
              },
              options: timeFrameOptions,
              shouldFit: true,
            }}
          />
          <DropdownComponent
            config={{
              label: 'TimeFrame Combination',
              value: timeFrameCombination,
              handleValueChange: setTimeFrameCombination,
              options: timeFrameCombinationOptions,
              shouldFit: true,
              labelFormatter: (option: string) => {
                if (!option?.length) {
                  return option;
                }

                return option.split('_').join(' ');
              },
            }}
          />
        </div>
      )}

      <div className="single-profit-calcultion-divider" />

      <div
        style={{
          flex: 1,
        }}
        ref={rootRef}
      >
        <ProfitDistributionChart
          data={fileResult}
          setTimeFrameCombination={handleGridItemClick}
        />

        {isValidating || isValidatingFileResult || isValidatingSpcResult ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <LoadingIcons.Grid width={60} height={60} />
          </div>
        ) : (
          !!spcResult && (
            <>
              <SpcResultView data={spcResult} />
            </>
          )
        )}
      </div>
    </>
  );
};

export default CalculationResultHistory;
